import { FormInstance, Input } from 'antd';
import { formatPhoneNumberIntl, isPossiblePhoneNumber } from 'react-phone-number-input';
import {
  FormAttachment,
  FormDatePicker,
  FormField,
  FormLabel,
  FormNumber,
  FormPhoneNumber,
  FormSelect,
  FormTextArea,
  FormTitle,
} from '../components/form/FormBasic';
import { REGEX_EMAIL } from '../constants/regex';
import { FormFieldTypeEnum } from '../enums/FormFieldTypeEnum';
import { DynamicForm } from '../types/FormFieldTypes';
import RESOURCE_CONFIGS from '../constants/resourceConfig';
import { Rule } from 'antd/es/form';

export const customizeRequiredMark = (
  label: React.ReactNode,
  { required }: { required: boolean }
) => (
  <>
    {label}
    &nbsp;
    {required && <span style={{ color: 'red' }}>*</span>}
  </>
);

export const renderField = (
  form: FormInstance,
  field: DynamicForm,
  t: any,
  fieldChangeTrigger?: (fieldValue: any, field: DynamicForm) => void,
  event?: any,
  extraProps?: any
) => {
  switch (field.type) {
    case FormFieldTypeEnum.INPUT:
      return (
        <FormField
          name={field.fieldId}
          label={t(field.label)}
          type={FormFieldTypeEnum.INPUT}
          isRequired={field.required}
          rules={field.rules}
          disabled={field.disabled}
          allowLeadingTrailingSpaces={true}
          onChange={(e: any) => {
            event && event(e.target.value, field);
          }}
        ></FormField>
      );
    case FormFieldTypeEnum.TITLE:
      return <FormTitle label={t(field.label)}></FormTitle>;
    case FormFieldTypeEnum.LABEL:
      return <FormLabel label={t(field.label)}></FormLabel>;
    case FormFieldTypeEnum.NUMBER:
      return (
        <FormNumber
          name={field.fieldId}
          label={t(field.label)}
          isRequired={field.required}
          disabled={true}
          rules={field.rules}
          min={field.min}
          max={field.max}
          addonAfter={field.addonAfter ? field.addonAfter : ''}
          onChange={(value: any) => {
            event && event(value, field);
          }}
        />
      );
    case FormFieldTypeEnum.SELECT:
      return (
        <FormSelect
          name={field.fieldId}
          label={t(field.label)}
          options={
            field.values?.map((value: any) => {
              return { ...value, label: t(value.label) };
            }) || []
          }
          isRequired={field.required}
          onChange={(value) => {
            fieldChangeTrigger?.(value, field);
            event && event(value, field);
          }}
          rules={field.rules ? field.rules : ''}
          disabled={field.disabled}
        ></FormSelect>
      );
    case FormFieldTypeEnum.TELEPHONE:
      return (
        <FormPhoneNumber
          name={field.fieldId}
          label={t(field.label)}
          isRequired={false}
          rules={
            field.rules
              ? field.rules
              : [
                  {
                    required: true,
                    message: '',
                  },
                ]
          }
        ></FormPhoneNumber>
      );
    case FormFieldTypeEnum.EMAIL:
      return (
        <FormField
          name={field.fieldId}
          label={t(field.label)}
          type={FormFieldTypeEnum.INPUT}
          isRequired={true}
          allowLeadingTrailingSpaces={false}
          rules={
            field.rules
              ? field.rules
              : [
                  {
                    required: field.required,
                    message: t('form:required'),
                  },
                  {
                    pattern: REGEX_EMAIL,
                    message: t('form:invalidEmail'),
                  },
                ]
          }
        ></FormField>
      );
    case FormFieldTypeEnum.DATE:
      return (
        <FormDatePicker
          name={field.fieldId}
          label={t(field.label)}
          isRequired={field.required}
          rules={field.rules}
          maxDate={field.maxDate}
          format={field.format || RESOURCE_CONFIGS.DATE_TIME.DEFAULT_FORMAT}
          selectFutureDatesOnly={field.selectFutureDatesOnly}
        ></FormDatePicker>
      );

    case FormFieldTypeEnum.DATE_TIME:
      return (
        <FormDatePicker
          showTime={true}
          name={field.fieldId}
          label={t(field.label)}
          rules={field.rules}
          isRequired={field.required}
          maxDate={field.maxDate}
        ></FormDatePicker>
      );

    case FormFieldTypeEnum.ATTACHMENT: {
      let initialFileList = extraProps?.initialFiles || [];
      return (
        <FormAttachment
          name={field.fieldId}
          label={t(field.label)}
          form={form}
          rules={field.rules}
          isRequired={field.required}
          maxCount={field.maxCount}
          fileTypes={field.fileTypes}
          initialFiles={initialFileList}
          isDisabled={extraProps?.isDisabled}
        />
      );
    }
    case FormFieldTypeEnum.TEXT_AREA:
      return (
        <FormTextArea
          name={field.fieldId}
          label={t(field.label)}
          rules={field.rules}
          isRequired={field.required}
        ></FormTextArea>
      );
    default:
      return (
        <FormField
          name={field.fieldId}
          label={t(field.label)}
          type={field.type as FormFieldTypeEnum}
          isRequired={field.required}
          rules={field.rules}
        ></FormField>
      );
  }
};

export const renderViewField = (
  form: FormInstance,
  field: DynamicForm,
  t: any,
  setUploadInProgress: (status: boolean) => void
) => {
  switch (field.type) {
    case FormFieldTypeEnum.TITLE:
      return <FormTitle label={t(field.label)}></FormTitle>;
    case FormFieldTypeEnum.LABEL:
      return <FormLabel label={t(field.label)}></FormLabel>;
    case FormFieldTypeEnum.SELECT:
      return (
        <FormSelect
          name={field.fieldId}
          label={t(field.label)}
          options={
            field.values?.map((value: any) => {
              return { ...value, label: t(value.label) };
            }) || []
          }
          isRequired={field.required}
        ></FormSelect>
      );
    case FormFieldTypeEnum.TELEPHONE:
      return (
        <FormPhoneNumber
          name={field.fieldId}
          label={t(field.label)}
          isRequired={field.required}
        ></FormPhoneNumber>
      );
    case FormFieldTypeEnum.DATE:
      return (
        <FormDatePicker
          name={field.fieldId}
          label={t(field.label)}
          isRequired={field.required}
          maxDate={field.maxDate}
        ></FormDatePicker>
      );
    case FormFieldTypeEnum.DATE_TIME:
      return (
        <FormDatePicker
          showTime={true}
          name={field.fieldId}
          label={t(field.label)}
          rules={field.rules}
          isRequired={field.required}
          maxDate={field.maxDate}
        ></FormDatePicker>
      );
    case FormFieldTypeEnum.ATTACHMENT:
      return (
        <FormAttachment
          name={field.fieldId}
          label={t(field.label)}
          form={form}
          rules={field.rules}
          isRequired={field.required}
          maxCount={field.maxCount}
        ></FormAttachment>
      );
    case FormFieldTypeEnum.TEXT_AREA:
      return (
        <FormTextArea
          name={field.fieldId}
          label={t(field.label)}
          rules={field.rules}
          isRequired={field.required}
        ></FormTextArea>
      );
    default:
      return (
        <FormField
          name={field.fieldId}
          label={t(field.label)}
          type={field.type as FormFieldTypeEnum}
          isRequired={field.required}
          rules={field.rules}
        ></FormField>
      );
  }
};

export const fieldChangeActionHandler = (
  workflowVisibleFields: DynamicForm[],
  fieldValue: any,
  option: DynamicForm,
  dependencyFields?: DynamicForm[]
) => {
  let currentFields = [...workflowVisibleFields];
  if (option.dependency) {
    const { value, fields } = option.dependency;

    if (!fields) return;

    if (value === fieldValue) {
      const dependecyField = dependencyFields?.filter((cField) => fields.includes(cField.fieldId));
      if (dependecyField) {
        let index = currentFields.findIndex((field) => field.fieldId === option.fieldId);
        currentFields.splice(index + 1, 0, ...dependecyField);
        return currentFields;
      }
      return [...currentFields];
    }

    return currentFields?.filter((cField) => !fields.includes(cField.fieldId));
  }

  if (option.type === FormFieldTypeEnum.SELECT && Array.isArray(option.values)) {
    const selectedValue = option.values.find((val) => val.value === fieldValue);

    if (!selectedValue) return currentFields;

    const requiredFieldIds = selectedValue.requiredFields || [];
    const optionalFieldIds = selectedValue.optionalFields || [];
    const allTargetFieldIds = [...requiredFieldIds, ...optionalFieldIds];
    const allOptionFieldIds = option.values
      .flatMap((val) => [...(val.requiredFields || []), ...(val.optionalFields || [])])
      .filter((v, i, arr) => arr.indexOf(v) === i);

    // Loop through existing fields and update `required` if in affected list
    currentFields = currentFields.map((field) => {
      if (allOptionFieldIds.includes(field.fieldId)) {
        return {
          ...field,
          required: requiredFieldIds.includes(field.fieldId),
        };
      }
      return field;
    });

    // Inject new fields from dependencyFields if not already visible
    const newInjectables =
      dependencyFields?.filter(
        (f) =>
          allTargetFieldIds.includes(f.fieldId) &&
          !currentFields.some((cf) => cf.fieldId === f.fieldId)
      ) || [];

    const enrichedInjectedFields = newInjectables.map((field) => ({
      ...field,
      required: requiredFieldIds.includes(field.fieldId),
    }));

    const index = currentFields.findIndex((field) => field.fieldId === option.fieldId);
    currentFields.splice(index + 1, 0, ...enrichedInjectedFields);

    return currentFields;
  }

  return currentFields;
};

export const getFormSubmitObject = (form: FormInstance, dynamicForm: DynamicForm[]) => {
  const formValues: any = {};
  dynamicForm
    .filter((formField) => !formField.hidden)
    .forEach((field) => {
      switch (field.type) {
        case FormFieldTypeEnum.DATE:
          formValues[field.fieldId] = form.getFieldValue(field.fieldId).valueOf();
          break;
        default:
          formValues[field.fieldId] = form.getFieldValue(field.fieldId);
      }
    });

  return formValues;
};

export const validationRules = (t: any) => {
  return {
    PASSWORD: (_: any, value: any) => {
      if (!value) {
        return Promise.reject(new Error(t('enterPassword')));
      }
      if (value.length < 8) {
        return Promise.reject(new Error(t('passwordMinLength')));
      }
      if (!/[A-Z]/.test(value)) {
        return Promise.reject(new Error(t('passwordUpperCase')));
      }
      if (!/[a-z]/.test(value)) {
        return Promise.reject(new Error(t('passwordLowerCase')));
      }
      if (!/[0-9]/.test(value)) {
        return Promise.reject(new Error(t('passwordNumber')));
      }
      if (!/[!@#$^*()_+;:?]/.test(value)) {
        return Promise.reject(new Error(t('passwordSpecialCharacter')));
      }
      if (/\s/.test(value)) {
        return Promise.reject(new Error(t('passwordNoSpaces')));
      }
      return Promise.resolve();
    },
    PASSWORD_REQUIRED_ONLY: (_: any, value: any) => {
      if (!value) {
        return Promise.reject(new Error(t('enterPassword')));
      }
      return Promise.resolve();
    },
  };
};

export const resetFormFields = (form: FormInstance, formFields: any) => {
  let fields: Record<string, any> = {};
  formFields.map((field: any) => {
    fields[field.fieldId] = '';
  });

  form.setFieldsValue(fields);
  form.resetFields();
};

export const phoneNumberValidator = (t: any) => async (_: any, value: any) => {
  const trimmed = String(value || '').trim();
  if (!trimmed) {
    throw new Error(t('form:phoneRequired'));
  }
  const phoneNo = formatPhoneNumberIntl(trimmed);
  if (!phoneNo || !isPossiblePhoneNumber(trimmed)) {
    throw new Error(t('invalidInput'));
  }
};

export const validatePhoneNumberAsync = async (t: any, value: any): Promise<boolean> => {
  try {
    await phoneNumberValidator(t)(null, value);
    return true;
  } catch (err) {
    return false;
  }
};

export const requiredRule = (t: any, fieldName: string): Rule => ({
  required: true,
  message: t('form:isRequired', { field: fieldName, interpolation: { escapeValue: false } }),
});

export const noSpacesOnlyRule = (t: any, fieldName: string): Rule => ({
  validator: (_, value) => {
    if (value && String(value).trim() === '') {
      return Promise.reject(
        t('form:spaceValidation', { field: fieldName, interpolation: { escapeValue: false } })
      );
    }
    return Promise.resolve();
  },
});

export const regexRule = (t: any, pattern: RegExp): Rule => ({
  validator: (_, value) => {
    if (!value) return Promise.resolve();
    return pattern.test(String(value)) ? Promise.resolve() : Promise.reject(t('form:invalidInput'));
  },
});

export const minLengthRule = (t: any, fieldName: string, min: number): Rule => ({
  validator: (_, value) => {
    if (!value || String(value).trim() === '') {
      return Promise.resolve();
    }
    const trimmedLength = String(value).replace(/\s/g, '').length;
    if (trimmedLength < min) {
      return Promise.reject(
        t('form:minValidation', { field: fieldName, min, interpolation: { escapeValue: false } })
      );
    }
    return Promise.resolve();
  },
});

export const maxLengthRule = (t: any, fieldName: string, max: number): Rule => ({
  validator: (_, value) => {
    if (!value || String(value).trim() === '') {
      return Promise.resolve();
    }
    const trimmedLength = String(value).replace(/\s/g, '').length;
    if (trimmedLength > max) {
      return Promise.reject(
        t('form:maxValidation', { field: fieldName, max, interpolation: { escapeValue: false } })
      );
    }
    return Promise.resolve();
  },
});

export const positiveNumberRule = (t: any, fieldName: string): Rule => ({
  validator: (_, value) => {
    if (value === undefined || value === null || value === '') {
      return Promise.resolve();
    }

    return Number(value) > 0
      ? Promise.resolve()
      : Promise.reject(t('form:positiveNumberValidation', { field: fieldName }));
  },
});
